import { useState, useCallback, useEffect } from 'react';
import { Slide } from '../types/index';

export function useSlideManagement(initialSlides: Slide[], onUpdateSlides: (slides: Slide[]) => void) {
  const [editingSlides, setEditingSlides] = useState<Slide[]>(initialSlides);

  // Sync with props
  useEffect(() => {
    if (JSON.stringify(initialSlides) !== JSON.stringify(editingSlides)) {
      setEditingSlides(initialSlides);
    }
  }, [initialSlides]);

  const updateSlide = useCallback((index: number, updates: Partial<Slide>) => {
    const newSlides = [...editingSlides];
    newSlides[index] = {
      ...newSlides[index],
      ...updates
    };
    setEditingSlides(newSlides);
    onUpdateSlides(newSlides);
  }, [editingSlides, onUpdateSlides]);

  const moveSlide = useCallback((index: number, direction: 'up' | 'down') => {
    if (
      (direction === 'up' && index === 0) ||
      (direction === 'down' && index === editingSlides.length - 1)
    ) {
      return;
    }

    const newSlides = [...editingSlides];
    const adjacentIndex = direction === 'up' ? index - 1 : index + 1;
    [newSlides[index], newSlides[adjacentIndex]] = [newSlides[adjacentIndex], newSlides[index]];

    setEditingSlides(newSlides);
    onUpdateSlides(newSlides);
  }, [editingSlides, onUpdateSlides]);

  const addSlide = useCallback((newSlide: Slide, position?: { type: 'above' | 'below', index: number }) => {
    const newSlides = [...editingSlides];
    const targetIndex = position ? (
      position.type === 'above' ? position.index : position.index + 1
    ) : newSlides.length;
    
    newSlides.splice(targetIndex, 0, newSlide);
    setEditingSlides(newSlides);
    onUpdateSlides(newSlides);
    return targetIndex;
  }, [editingSlides, onUpdateSlides]);

  const removeSlide = useCallback((index: number) => {
    const newSlides = editingSlides.filter((_, i) => i !== index);
    setEditingSlides(newSlides);
    onUpdateSlides(newSlides);
  }, [editingSlides, onUpdateSlides]);

  return {
    editingSlides,
    setEditingSlides,
    updateSlide,
    moveSlide,
    addSlide,
    removeSlide
  };
} 